import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Loader2, Megaphone, Trash2, Plus } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";

const inputCls = "w-full bg-[#0A0A0A] border border-white/15 focus:border-white outline-none px-3 py-2.5 text-sm transition-colors";

export default function AnnouncementsTab() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ title: "", body: "" });
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try { const { data } = await api.get("/announcements"); setItems(data); }
    catch { toast.error("Failed to load announcements."); } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const create = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post("/announcements", { title: form.title, body: form.body, scope: "global" });
      toast.success("Announcement posted to everyone."); setForm({ title: "", body: "" }); load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); } finally { setSaving(false); }
  };
  const del = async (id) => { try { await api.delete(`/announcements/${id}`); toast.success("Deleted."); load(); } catch { toast.error("Delete failed."); } };

  return (
    <div data-testid="announcements-tab">
      <h1 className="font-display font-black uppercase tracking-tighter text-4xl md:text-5xl mb-8">Announcements</h1>

      <form onSubmit={create} className="bg-[#121212] border border-white/10 p-6 mb-8 space-y-4 max-w-2xl" data-testid="admin-ann-form">
        <input data-testid="ann-title" required placeholder="Title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className={inputCls} />
        <textarea data-testid="ann-body" required placeholder="Message to all platform users" rows={3} value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} className={inputCls} />
        <button data-testid="ann-submit" disabled={saving} className="inline-flex items-center gap-2 bg-white text-black font-bold uppercase tracking-widest text-xs px-6 py-3 hover:bg-white/85 transition-colors disabled:opacity-60">
          {saving ? <Loader2 size={14} className="animate-spin" /> : <Megaphone size={14} />} Post to Everyone
        </button>
      </form>

      {loading ? <div className="flex justify-center py-24 text-white/40"><Loader2 className="animate-spin" /></div> : items.length === 0 ? (
        <div className="text-white/40 text-sm border border-white/10 bg-[#121212] p-6" data-testid="ann-empty">No announcements yet.</div>
      ) : (
        <div className="space-y-3" data-testid="ann-list">
          {items.map((a) => (
            <div key={a.id} data-testid={`ann-${a.id}`} className="bg-[#121212] border border-white/10 p-5 flex justify-between gap-4">
              <div>
                <div className="flex items-center gap-2 mb-1"><Megaphone size={14} className="text-white/50" /><p className="font-bold">{a.title}</p>
                  <span className="font-mono-accent text-[10px] tracking-[0.2em] uppercase border border-white/20 px-2 py-0.5 ml-1">{a.scope}</span></div>
                <p className="text-white/60 text-sm">{a.body}</p>
                <p className="text-white/30 text-[10px] mt-2">{a.author_name} · {new Date(a.created_at).toLocaleString()}</p>
              </div>
              <button data-testid={`ann-delete-${a.id}`} onClick={() => del(a.id)} className="text-white/30 hover:text-red-400 transition-colors shrink-0"><Trash2 size={16} /></button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
